import { createPopper, popper } from '@popperjs/core';
import _ from 'lodash';
import moment from 'moment';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled, { css } from 'styled-components';
import {
  ActionsBanner,
  BillableIcon,
  ColumnsButton,
  ColumnsDrawer,
  Confirmation,
  DateTime,
  DeleteConfirmation,
  ExportDialog,
  FiltersBar,
  HoursProgress,
  Icon,
  ListView,
  ListViewActions,
  ListViewMenu,
  ListViewSelection,
  Page,
  RevenueProgress,
  SearchInput,
  Tag,
  TagsList,
  TaskCategoryIcon,
  Tooltip,
} from '~/components';
import { BulkActionMenu } from '~/components/bulk-action';
import { FilterButton, TaskFiltersBar } from '~/components/filters';
import { useApi, useConfirmation, useSubscription, useToast, useWorkspace } from '~/contexts';
import {
  useDocumentTitle,
  useFeatures,
  useRowSelection,
  useSearchParams,
  useSearchParamsConfig,
  useViewColumns,
} from '~/hooks';
import projectTaskCategoryTypes from '~/lookups/project-task-category-types';
import { PageLoader } from '~/routes/public/pages';
import { colors, weights } from '~/styles';
import { dateFormats, QueryString } from '~/utils';
import ProjectTaskCloneConfirmation from '../../project-tasks/ProjectTaskCloneConfirmation';
import ProjectTaskChangeCategoryConfirmation from '../../project-tasks/ProjectTaskChangeCategoryConfirmation';
import MoveAllocationsConfirmation from './MoveAllocationsConfirmation';
import MoveTimeConfirmation from './MoveTimeConfirmation';
import ProjectTaskCreateForm from '../../project-tasks/ProjectTaskCreateForm';
import ProjectTaskDetailsDrawer from '../../project-tasks/ProjectTaskDetailsDrawer';
import ProjectTaskEditDrawer from '../../project-tasks/ProjectTaskEditDrawer';
import EditTimeEntry from '../../time/edit-time-entry';
import { BulkArchive, BulkChangeCategory, BulkDelete, BulkSetStatus, BulkUnarchive } from './bulk';
import Assignments from './components/Assignments';
import buildTree from './components/buildTree';
import ExportTasksAction from './components/ExportTasksAction';
import NewTaskAction from './components/NewTaskAction';
import Node from './components/Node';
import TaskFiltersDrawer from './components/TaskFiltersDrawer';
import TasksViewNav from './components/TasksViewNav';
import Tree from './components/Tree';

const offset = 140;
const indentPerLevel = 16;
const indent = (depth) => depth * indentPerLevel;
const indentWithOffset = (depth) => (depth === 0 ? 0 : offset + depth * indentPerLevel);

const Styled = {
  Filters: styled(Page.Filters)`
    margin-top: 1.5rem;
  `,

  DragHandle: styled.div`
    color: ${colors.grey40};
    width: 1rem;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: grab;
    height: 100%;
    position: relative;

    &:hover {
      color: ${colors.grey75};
    }

    z-index: 2;

    ${({ $disabled }) =>
      $disabled &&
      css`
        cursor: not-allowed;
        color: ${colors.grey20};
        &:hover {
          color: ${colors.grey20};
        }
      `}

    &::before {
      content: '';
      position: absolute;
      top: -8px;
      bottom: -8px;
      left: -8px;
      right: -8px;
      z-index: -1;
    }
  `,

  DragPreview: styled.div`
    display: flex;
    align-items: center;
    padding: 0.25rem 0.5rem;
    font-size: 0.875rem;
    margin-top: 0.75rem;
    margin-left: 0.75rem;
    border: 2px solid ${colors.primary};
    border-radius: 0.25rem;
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
    pointer-events: none;
    cursor: grab;
    min-width: 20rem;
  `,

  DropIndicator: styled.div`
    position: absolute;
    right: 0;
    height: 2px;
    background-color: ${({ $instruction }) =>
      $instruction?.type === 'instruction-blocked' ? colors.red : colors.primary};
    pointer-events: none;
    z-index: 10;
    display: flex;
    align-items: center;

    &::before {
      content: '';
      width: 0.625rem;
      height: 0.625rem;
      border-radius: 50%;
      background-color: ${({ $instruction }) =>
        $instruction?.type === 'instruction-blocked' ? colors.red : colors.primary};
      margin-right: 4px;
    }

    ${({ $instruction }) => {
      const instruction = $instruction.type === 'instruction-blocked' ? $instruction.desired : $instruction;

      switch (instruction.type) {
        case 'reorder-above':
          return css`
            top: -1px;
            left: ${`${indentWithOffset(instruction.currentLevel)}px`};
          `;

        case 'reorder-below':
          return css`
            bottom: -1px;
            left: ${`${indentWithOffset(instruction.currentLevel)}px`};
          `;

        case 'reparent':
          return css`
            bottom: -1px;
            left: ${({ $instruction }) => `${indentWithOffset($instruction.desiredLevel)}px`};

            &::before {
              background-color: ${colors.primary};
            }
          `;

        default:
          return css`
            display: none;
          `;
      }
    }}
  `,

  Branch: styled.div`
    position: relative;
    font-size: 0.75rem;
    width: 1rem;
    margin-left: ${({ depth }) => `${indent(depth)}px`};
    color: ${colors.grey40};
    cursor: pointer;
    flex-shrink: 0;
    height: 100%;
    display: flex;
    align-items: center;

    &:hover {
      color: ${colors.grey75};
    }

    ${({ $disabled }) =>
      $disabled &&
      css`
        cursor: not-allowed;
        color: ${colors.grey20};
        &:hover {
          color: ${colors.grey20};
        }
      `}

    &::before {
      content: '';
      position: absolute;
      top: -8px;
      bottom: -8px;
      left: -8px;
      right: -8px;
      z-index: -1;
    }
  `,

  Leaf: styled.span`
    font-size: 0.75rem;
    width: 1rem;
    margin-left: ${({ depth }) => `${indent(depth)}px`};
    flex-shrink: 0;
  `,

  TaskName: styled.div`
    margin-left: 0.5rem;
  `,

  TaskStatus: styled.span`
    display: flex;
    align-items: center;

    .icon {
      font-size: 0.625rem;
      margin-right: 0.7rem;
      color: ${({ status }) =>
        ({
          not_started: colors.warning,
          in_progress: colors.success,
          completed: colors.black,
        })[status]};
    }

    span {
      color: ${({ $isArchived }) => ($isArchived ? colors.grey40 : colors.black)};
    }
  `,

  Row: styled(ListView.Row)`
    position: relative;
    user-select: none;

    ${({ $instruction }) => {
      switch ($instruction?.type) {
        case 'make-child':
          return css`
            &::before {
              content: '';
              position: absolute;
              top: 0;
              left: 0;
              right: 0;
              bottom: 0;
              border: 2px solid ${colors.primary};
              z-index: 10;
              pointer-events: none;
            }
          `;

        case 'instruction-blocked':
          if ($instruction.desired.type !== 'make-child') return;

          return css`
            &::before {
              content: '';
              position: absolute;
              top: 0;
              left: 0;
              right: 0;
              bottom: 0;
              border: 2px solid ${colors.danger};
              z-index: 10;
              pointer-events: none;
            }
          `;
      }
    }}
  `,

  ExpandCollapse: styled.div`
    margin-right: 0.75rem;
    color: ${colors.grey40};

    &:hover {
      cursor: pointer;
      color: ${colors.grey75};
    }
  `,
};

export default function TasksList({ project, onViewChange, setRefetch }) {
  const api = useApi();
  const { workspace } = useWorkspace();
  const features = useFeatures();

  const [{ tasks, isLoading }, setTasks] = useState({ tasks: null, isLoading: true });

  const selection = useRowSelection({ rows: tasks });

  const [params, setParams] = useState({
    q: '',
    projectTaskRecordStatusId: '',
    projectTaskStatuses: [],
    projectTaskTags: [],
  });

  const toast = useToast();

  const searchParamsConfig = useSearchParamsConfig();
  const searchParams = useSearchParams({
    config: useMemo(
      () => ({
        q: { default: '' },
        projectTaskStatuses: searchParamsConfig.projectTaskStatuses,
        projectTaskRecordStatusId: { default: 'active', ...searchParamsConfig.projectTaskRecordStatusId },
        projectTaskTags: searchParamsConfig.projectTaskTags,
      }),
      [searchParamsConfig],
    ),
    sessionKey: 'project_dashboard_tasks_list',
    onChange: useCallback((params) => setParams((state) => ({ ...state, ...params })), []),
  });
  const [searchParamsStatus, setSearchParamsStatus] = useState('pending');

  useEffect(() => {
    if (searchParamsStatus === 'ready') return;
    searchParams.get().then((params) => {
      if (params) {
        setParams((state) => ({ ...state, ...params }));
        setSearchParamsStatus('ready');
      }
    });
  }, [searchParams, searchParamsStatus]);

  const fetchData = useCallback(
    async (p = params) => {
      const urlSearchParams = {
        projectId: project.id,
        q: p.q,
        projectTaskStatusId: p.projectTaskStatuses?.map((v) => v.id),
        projectTaskRecordStatusId: p.projectTaskRecordStatusId ?? undefined,
        projectTaskTagId: p.projectTaskTags?.map((v) => v.id),
      };

      try {
        setTasks((state) => ({ ...state, isLoading: true }));
        const { data } = await api.www.workspaces(workspace.id).projects(project.id).tasks().list.get(urlSearchParams);
        setTasks({ tasks: data, isLoading: false });
        return data;
      } catch (error) {
        setTasks({ tasks: [], isLoading: false });
        toast.error(error.message);
      }
    },
    [api, workspace.id, toast, project.id, params],
  );

  const handleFilter = async (filter) => {
    const newFilters = { ...params, ...filter };
    setParams(newFilters);
    searchParams.set(newFilters);
    selection.clear();

    const data = await fetchData(newFilters);
    if (!data) return;
  };

  const handleShowFiltersDrawer = () => {
    setDialog(<TaskFiltersDrawer values={params} onApply={handleFilter} onClose={() => setDialog(null)} />);
  };

  useEffect(() => {
    if (searchParamsStatus !== 'ready') return;
    if (tasks) return;
    fetchData();
  }, [fetchData, searchParamsStatus, tasks]);

  useEffect(() => {
    if (setRefetch) {
      setRefetch(() => fetchData);
    }
  }, [setRefetch, fetchData]);

  const [expandedMap, setExpandedMap] = useState(() => new Map());

  const getExpanded = useCallback((node) => expandedMap.get(node.id) ?? false, [expandedMap]);

  const setExpanded = useCallback((id, value) => {
    setExpandedMap((prev) => {
      const next = new Map(prev);
      next.set(id, value);
      return next;
    });
  }, []);

  const nodes = useMemo(
    () =>
      tasks
        ? buildTree(
            _(tasks)
              .orderBy('position')
              .map((task) => ({
                id: task.id,
                parentId: task.parentId,
                position: task.position,
                type: task.type,
                isBillable: task.isBillable,
                expanded: task.resultType === 'ancestor' || getExpanded(task),
                resultType: task.resultType,
                task,
              }))
              .value(),
          )
        : null,
    [tasks, getExpanded],
  );

  const toggleAll = useMemo(
    () => (tasks?.some((task) => expandedMap.get(task.id) === true) ? 'collapse' : 'expand'),
    [expandedMap, tasks],
  );

  const expandAll = useCallback(() => {
    const allIds = getAllNodeIds(nodes);
    setExpandedMap(new Map(allIds.map((id) => [id, true])));
  }, [nodes]);

  const collapseAll = useCallback(() => {
    const next = new Map(expandedMap);
    for (const { id } of tasks) next.set(id, false);
    setExpandedMap(next);
  }, [expandedMap, tasks]);

  const handleMove = useCallback(
    async (node) => {
      if (!node) return;

      const task = {
        ...tasks.find((t) => t.id === node.id),
        parentId: node.parentId,
        position: node.position,
      };

      setTasks((state) => ({ ...state, tasks: state.tasks.map((t) => (t.id === task.id ? task : t)) }));
      setExpanded(node.parentId, true);

      try {
        await api.www.workspaces(workspace.id).projects(project.id).tasks(task.id).list.update({
          parentId: node.parentId,
          insertBeforeId: node.insertBeforeId,
          insertAfterId: node.insertAfterId,
        });
      } catch (error) {
        toast.error(error.message);
      }

      await fetchData();
    },
    [api, tasks, workspace.id, setExpanded, fetchData, project.id, toast],
  );

  const [dialog, setDialog] = useState(null);

  const handleCloseDialog = () => {
    documentTitle.set(project.name);
    setDialog(null);
  };

  const confirmation = useConfirmation();

  const handleBulkDelete = async () => {
    await confirmation.prompt((resolve) => (
      <BulkDelete
        projectId={project.id}
        useDependencies={project.useDependencies}
        ids={selection.ids}
        onComplete={async () => {
          selection.clear();
          await fetchData();
        }}
        onClose={resolve}
      />
    ));
  };

  const handleBulkArchive = async () => {
    await confirmation.prompt((resolve) => (
      <BulkArchive
        projectId={project.id}
        ids={selection.ids}
        onComplete={async () => {
          selection.clear();
          await fetchData();
        }}
        onClose={resolve}
      />
    ));
  };

  const handleBulkUnarchive = async () => {
    await confirmation.prompt((resolve) => (
      <BulkUnarchive
        projectId={project.id}
        ids={selection.ids}
        onComplete={async () => {
          selection.clear();
          await fetchData();
        }}
        onClose={resolve}
      />
    ));
  };

  const handleBulkSetStatus = async () => {
    await confirmation.prompt((resolve) => (
      <BulkSetStatus
        projectId={project.id}
        ids={selection.ids}
        onComplete={async () => {
          selection.clear();
          await fetchData();
        }}
        onClose={resolve}
      />
    ));
  };

  const handleBulkChangeCategory = async () => {
    await confirmation.prompt((resolve) => (
      <BulkChangeCategory
        projectId={project.id}
        ids={selection.ids}
        onComplete={async () => {
          selection.clear();
          await fetchData();
        }}
        onClose={resolve}
      />
    ));
  };

  const handleEdit = (task) => {
    setDialog(
      <ProjectTaskEditDrawer
        taskId={task.id}
        project={project}
        onClose={handleCloseDialog}
        onSaved={async () => {
          await fetchData();
          handleCloseDialog();
        }}
        onDelete={async () => {
          const confirmMessage =
            project.useDependencies && (task.predecessors?.length > 0 || task.successors?.length > 0)
              ? 'Are you sure you want to delete this task? Any task dependencies will also be removed.'
              : 'Are you sure you want to delete this task?';

          const confirm = await confirmation.prompt((resolve) => (
            <DeleteConfirmation resolve={resolve}>{confirmMessage}</DeleteConfirmation>
          ));
          if (!confirm) {
            return;
          }

          try {
            await api.www.workspaces(workspace.id).projects(project.id).tasks(task.id).delete();
            await fetchData();
            handleCloseDialog();
          } catch ({ message }) {
            toast.error(message);
          }
        }}
      />,
    );
  };

  const handleView = (task) => {
    setDialog(<ProjectTaskDetailsDrawer taskId={task.id} project={project} onClose={handleCloseDialog} />);
  };

  const handleExport = async (filename, mimeType) => {
    const p = params;
    const urlSearchParams = {
      projectId: project.id,
      q: p.q,
      projectTaskStatusId: p.projectTaskStatuses?.map((v) => v.id),
      projectTaskRecordStatusId: p.projectTaskRecordStatusId ?? undefined,
      projectTaskTagId: p.projectTaskTags?.map((v) => v.id),
    };

    confirmation.prompt((resolve) => (
      <ExportDialog
        filename={filename}
        onLoad={api.www
          .workspaces(workspace.id)
          .projects(project.id)
          .tasks()
          .getExport(urlSearchParams, {
            headers: { accept: mimeType },
            responseType: 'blob',
          })}
        onClose={resolve}
      />
    ));
  };

  const documentTitle = useDocumentTitle();

  function isRollupWithChildren(task) {
    return task.type === 'rollup' && tasks.some((t) => t.parentId === task.id);
  }

  const { notify } = useSubscription();
  const handleArchive = async (task) => {
    const message = isRollupWithChildren(task)
      ? 'Are you sure that you want to archive this rollup task? All subtasks will also be archived.'
      : 'Are you sure that you want to archive this task?';

    const confirm = await confirmation.prompt((resolve) => <Confirmation resolve={resolve}>{message}</Confirmation>);
    if (!confirm) return;

    try {
      await api.www
        .workspaces(workspace.id)
        .projects(project.id)
        .tasks(task.id)
        .setRecordStatus({ recordStatusId: 'archived' });
      notify(useSubscription.keys.refresh_timer);
      await fetchData();
    } catch ({ message }) {
      toast.error(message);
    }
  };

  const handleUnarchive = async (task) => {
    const hasArchivedParent = task.parentId && tasks.find((t) => t.id === task.parentId)?.recordStatusId === 'archived';
    const message = isRollupWithChildren(task)
      ? 'Are you sure that you want to unarchive this rollup task? All subtasks will also be unarchived.'
      : hasArchivedParent
        ? 'Are you sure that you want to unarchive this task? The parent rollup task will also be unarchived.'
        : 'Are you sure that you want to unarchive this task?';

    const confirm = await confirmation.prompt((resolve) => <Confirmation resolve={resolve}>{message}</Confirmation>);
    if (!confirm) return;

    try {
      await api.www
        .workspaces(workspace.id)
        .projects(project.id)
        .tasks(task.id)
        .setRecordStatus({ recordStatusId: 'active' });
      notify(useSubscription.keys.refresh_timer);
      await fetchData();
    } catch ({ message }) {
      toast.error(message);
    }
  };

  async function handleDelete(task) {
    const confirmMessage =
      task.predecessors?.length > 0 || task.successors?.length > 0
        ? 'Are you sure you want to delete this task? Any task dependencies will also be removed.'
        : 'Are you sure you want to delete this task?';

    const confirm = await confirmation.prompt((resolve) => (
      <DeleteConfirmation resolve={resolve}>{confirmMessage}</DeleteConfirmation>
    ));
    if (!confirm) {
      return;
    }

    try {
      await api.www.workspaces(workspace.id).projects(project.id).tasks(task.id).delete();
      await fetchData();
    } catch ({ message }) {
      toast.error(message);
    }
  }

  async function handleClone(task) {
    setDialog(
      <ProjectTaskCloneConfirmation
        target={{ id: task.id, name: task.name, projectId: project.id }}
        onClose={handleCloseDialog}
        onSaved={async () => {
          await fetchData();
          handleCloseDialog();
        }}
      />,
    );
  }

  async function handleChangeType(task, targetType) {
    setDialog(
      <ProjectTaskChangeCategoryConfirmation
        task={{ id: task.id, name: task.name, type: task.type, projectId: project.id }}
        targetType={targetType}
        onClose={handleCloseDialog}
        onChanged={async () => {
          await fetchData();
          handleCloseDialog();
        }}
      />,
    );
  }

  async function handleMoveTime(task) {
    setDialog(
      <MoveTimeConfirmation task={task} project={project} onClose={handleCloseDialog} onComplete={() => fetchData()} />,
    );
  }

  async function handleMoveAllocations(task) {
    setDialog(
      <MoveAllocationsConfirmation
        task={task}
        project={project}
        onClose={handleCloseDialog}
        onComplete={() => fetchData()}
      />,
    );
  }

  async function handleTrackTime(task) {
    setDialog(
      <EditTimeEntry
        initialValues={{
          date: moment().format(dateFormats.isoDate),
          projectId: project.id,
          projectTaskId: task.id,
        }}
        onSaved={async () => {
          notify(useSubscription.keys.refresh_timer);
          await fetchData();
          handleCloseDialog();
        }}
        onClose={() => {
          handleCloseDialog();
          documentTitle.set(project.name);
        }}
      />,
    );
  }

  const columns = useViewColumns({
    storageKey: 'project_dashboard_tasks_list_columns',
    columns: [
      { key: 'name', name: 'Name', disabled: true },
      { key: 'code', name: 'Code', default: true },
      { key: 'status', name: 'Status', default: true },
      { key: 'start', name: 'Start Date', default: true },
      { key: 'end', name: 'End Date', default: true },
      { key: 'assignedTo', name: 'Assigned To', default: true },
      { key: 'hours', name: 'Hours', default: true },
      { key: 'revenue', name: 'Revenue', default: true },
      { key: 'tags', name: 'Tags' },
    ],
  });

  const handleShowColumnsDrawer = () => {
    setDialog(
      <ColumnsDrawer
        value={columns.selected}
        columns={columns.all}
        onClose={() => setDialog(null)}
        onApply={(value) => {
          columns.save(value);
          setDialog(null);
        }}
      />,
    );
  };

  const currency = project.currency;

  const showRevenue = useMemo(
    () =>
      project.permissions.viewRates &&
      project.permissions.viewRevenue &&
      ['tm', 'fixed', 'fixed_recurring'].includes(project.billingTypeId),
    [project],
  );

  function generateGetBoundingClientRect(x = 0, y = 0) {
    return () => ({
      width: 0,
      height: 0,
      top: y,
      right: x,
      bottom: y,
      left: x,
    });
  }

  const virtualElement = {
    getBoundingClientRect: generateGetBoundingClientRect(),
  };

  const instance = createPopper(virtualElement, popper);

  document.addEventListener('mousemove', ({ clientX: x, clientY: y }) => {
    virtualElement.getBoundingClientRect = generateGetBoundingClientRect(x, y);
    instance.update();
  });

  if (!nodes) return <PageLoader />;

  return (
    <>
      <Page.TabHeader>
        <Page.Title>Tasks</Page.Title>

        <TasksViewNav value="list" onChange={(view) => onViewChange(view)} />

        <Page.Actions>
          <ExportTasksAction
            projectKey={project.key}
            onExport={(filename, mimeType) => handleExport(filename, mimeType)}
          />

          <ColumnsButton onClick={handleShowColumnsDrawer} />

          <FilterButton onClick={() => handleShowFiltersDrawer()} />

          <NewTaskAction
            project={project}
            onSaved={async (data, action) => {
              await fetchData();
              if (action === 'save') {
                handleEdit(data);
              }
            }}
          />
        </Page.Actions>
      </Page.TabHeader>

      <Styled.Filters>
        <FiltersBar>
          <SearchInput
            value={params.q}
            placeholder="Search"
            onChange={({ target: { value } }) => handleFilter({ q: value })}
          />

          <TaskFiltersBar
            filters={params}
            sticky={['projectTaskStatuses', 'projectTaskRecordStatusId', 'projectTaskTags']}
            onChange={handleFilter}
          />
        </FiltersBar>
      </Styled.Filters>

      <Page.ListView>
        <Tree nodes={nodes} onMove={handleMove}>
          <ListView>
            <ListView.Header>
              <ListViewSelection.Column
                selection={selection}
                disabled={!tasks?.length || isLoading}
                isVisible={project.permissions.edit}
              />
              <ListView.Column width="0" isVisible={project.permissions.edit} />
              <ListView.Column width="3rem">
                <Icon icon="lock" color={colors.grey40} style={{ fontSize: '0.9rem' }} spaceLeft />
              </ListView.Column>
              <ListView.Column width="3rem" isVisible={project.isBillable}>
                <BillableIcon showTooltip={false} />
              </ListView.Column>
              <ListView.Column sticky minWidth="16rem">
                <Styled.ExpandCollapse>
                  {toggleAll === 'collapse' ? (
                    <Tooltip message="Collapse All" delay={500}>
                      <Icon icon="angle-double-up" onClick={() => collapseAll()} />
                    </Tooltip>
                  ) : (
                    <Tooltip message="Expand All" delay={500}>
                      <Icon icon="angle-double-down" onClick={() => expandAll()} />
                    </Tooltip>
                  )}
                </Styled.ExpandCollapse>
                Name
              </ListView.Column>
              <ListView.Column width="12rem" isVisible={columns.visible.code}>
                Code
              </ListView.Column>
              <ListView.Column width="9rem" isVisible={columns.visible.status}>
                Status
              </ListView.Column>
              <ListView.Column width="8rem" isVisible={columns.visible.start}>
                Start Date
              </ListView.Column>
              <ListView.Column width="8rem" isVisible={columns.visible.end}>
                End Date
              </ListView.Column>
              <ListView.Column width="18rem" isVisible={columns.visible.assignedTo}>
                Assigned To
              </ListView.Column>

              <ListView.Column
                width="12rem"
                isVisible={project.permissions.viewTimeAndExpenses && columns.visible.hours}>
                Hours
              </ListView.Column>
              <ListView.Column width="12rem" isVisible={showRevenue && columns.visible.revenue}>
                Revenue
              </ListView.Column>

              <ListView.Column width="12rem" isVisible={columns.visible.tags}>
                Tags
              </ListView.Column>
              <ListViewActions.Column />
            </ListView.Header>

            <ListView.Body>
              {nodes.map((node) => {
                return (
                  <Node
                    key={node.id}
                    node={node}
                    renderNode={({ node, depth, hasChildren, setDragElement, instruction }) => {
                      const task = node.task;
                      const isAncestor = node.resultType === 'ancestor';

                      return (
                        <>
                          {instruction && <Styled.DropIndicator $instruction={instruction} />}

                          <Styled.Row
                            $instruction={instruction}
                            isDisabled={task.recordStatusId === 'archived'}
                            onClick={task.permissions.manage ? () => handleEdit(task) : () => handleView(task)}>
                            <ListViewSelection.Cell
                              id={task.id}
                              selection={selection}
                              isVisible={project.permissions.edit}
                            />
                            <ListView.Cell>
                              <Styled.DragHandle
                                ref={setDragElement}
                                $disabled={isAncestor}
                                onClick={(e) => {
                                  e.stopPropagation();
                                }}>
                                <Tooltip
                                  message={isAncestor ? 'This is an ancestor of a matching task.' : undefined}
                                  delay={500}>
                                  <Icon icon="grip-vertical" />
                                </Tooltip>
                              </Styled.DragHandle>
                            </ListView.Cell>

                            <ListView.Cell>
                              {task.lockTime && (
                                <Tooltip message="Time has been locked for this task.">
                                  <Icon icon="lock" color={colors.grey40} spaceLeft />
                                </Tooltip>
                              )}
                            </ListView.Cell>

                            <ListView.Cell>
                              {task.type === 'standard' && <BillableIcon value={task.isBillable} />}
                            </ListView.Cell>

                            <ListView.Cell>
                              {hasChildren ? (
                                <Styled.Branch
                                  depth={depth}
                                  $disabled={isAncestor}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setExpanded(node.id, !node.expanded);
                                  }}>
                                  <Tooltip
                                    message={isAncestor ? 'This is an ancestor of a matching task.' : undefined}
                                    delay={500}>
                                    <Icon icon={node.expanded ? 'chevron-down' : 'chevron-right'} />
                                  </Tooltip>
                                </Styled.Branch>
                              ) : (
                                <Styled.Leaf depth={depth} />
                              )}

                              <TaskCategoryIcon icon={task.taskCategory.icon} color={task.taskCategory.color} />
                              <Styled.TaskName>{task.name}</Styled.TaskName>
                            </ListView.Cell>

                            <ListView.Cell>{task.code}</ListView.Cell>

                            <ListView.Cell>
                              <Styled.TaskStatus
                                status={task.status.id}
                                $isArchived={task.recordStatusId === 'archived'}>
                                <Icon icon="circle" />
                                <span>{task.status.name}</span>
                              </Styled.TaskStatus>
                            </ListView.Cell>

                            <ListView.Cell>
                              <DateTime value={task.start} />
                            </ListView.Cell>

                            <ListView.Cell>
                              <DateTime value={task.end} />
                            </ListView.Cell>

                            <ListView.Cell>
                              <Assignments task={task} project={project} />
                            </ListView.Cell>

                            <ListView.Cell>
                              <HoursProgress
                                worked={task.hours}
                                estimated={task.budgetHours}
                                isRollup={task.type === 'rollup'}
                              />
                            </ListView.Cell>

                            <ListView.Cell>
                              <RevenueProgress
                                earned={task.revenue}
                                budgeted={task.budgetRevenue}
                                currency={currency}
                                isRollup={task.type === 'rollup'}
                              />
                            </ListView.Cell>

                            <ListView.Cell>
                              {task.tags?.length > 0 && (
                                <TagsList style={{ fontSize: '0.75rem' }}>
                                  {task.tags.map(({ id, name }) => (
                                    <Tag key={id}>{name}</Tag>
                                  ))}
                                </TagsList>
                              )}
                            </ListView.Cell>

                            <ListViewActions>
                              {task.permissions.manage ? (
                                <ListViewActions.Edit onClick={() => handleEdit(task)} />
                              ) : (
                                <ListViewActions.View onClick={() => handleView(task)} />
                              )}

                              <hr />

                              <ListViewMenu>
                                {({ setIsOpen }) => (
                                  <>
                                    <ListViewMenu.Item onClick={() => setIsOpen(false) || handleView(task)}>
                                      View
                                    </ListViewMenu.Item>

                                    <ListViewMenu.Item
                                      disabled={!task.permissions.manage}
                                      tooltip={
                                        !task.permissions.manage
                                          ? "Insufficient permissions to modify this project's tasks."
                                          : undefined
                                      }
                                      onClick={() => setIsOpen(false) || handleEdit(task)}>
                                      Edit
                                    </ListViewMenu.Item>

                                    <ListViewMenu.Item
                                      disabled={!task.permissions.manage}
                                      onClick={() => setIsOpen(false) || handleClone(task)}>
                                      Clone
                                    </ListViewMenu.Item>

                                    <ListViewMenu.Item
                                      disabled={!task.permissions.manage || task.type === 'milestone'}
                                      tooltip={
                                        !task.permissions.manage
                                          ? 'Insufficient permissions to add a child task.'
                                          : task.type === 'milestone'
                                            ? 'A milestone cannot have subtasks.'
                                            : undefined
                                      }
                                      onClick={() => {
                                        setIsOpen(false);
                                        setDialog(
                                          <ProjectTaskCreateForm
                                            project={project}
                                            defaultValues={{ parent: task }}
                                            onClose={handleCloseDialog}
                                            onSaved={async (data, action) => {
                                              await fetchData();
                                              setExpanded(task.id, true);
                                              if (action === 'save') {
                                                handleEdit(data);
                                              }
                                            }}
                                          />,
                                        );
                                      }}>
                                      Add Child Task
                                    </ListViewMenu.Item>

                                    <ListViewMenu.Item
                                      onClick={() => setIsOpen(false) || handleTrackTime(task)}
                                      disabled={
                                        ['rollup', 'milestone'].includes(task.type) ||
                                        !task.isProjectMember ||
                                        task.project.recordStatusId !== 'active' ||
                                        (task.forAssignedOnly ? !task.assignedToMe : false) ||
                                        task.lockTime ||
                                        project.lockTimeAndExpenses ||
                                        task.recordStatusId === 'archived'
                                      }
                                      tooltip={(() => {
                                        if (task.type === 'rollup') return 'Time cannot be tracked on rollup tasks.';
                                        if (task.type === 'milestone')
                                          return 'Time cannot be tracked on milestone tasks.';
                                        if (!task.isProjectMember)
                                          return "In order to track time to this task, you must be an active project team member of this task's project.";
                                        if (task.project.recordStatusId !== 'active')
                                          return 'This project is archived.';
                                        if (task.forAssignedOnly && !task.assignedToMe)
                                          return 'In order to track time to this task, you or your project role must be assigned to the task.';
                                        if (project.lockTimeAndExpenses)
                                          return "This task's project does not allow adding new time entries.";
                                        if (task.lockTime) return 'This task does not allow adding new time entries.';
                                        if (task.recordStatusId === 'archived')
                                          return 'New time entries may not be associated with an archived task.';
                                      })()}>
                                      Track Time
                                    </ListViewMenu.Item>

                                    <ListViewMenu.Item
                                      disabled={!task.permissions.manage}
                                      tooltip={
                                        !task.permissions.manage
                                          ? "Insufficient permissions to delete this project's tasks."
                                          : undefined
                                      }
                                      onMount={async (setState) => {
                                        setState((state) => ({ ...state, disabled: true }));
                                        if (task.permissions.manage) {
                                          const { data: hasDependencies } = await api.www
                                            .workspaces(workspace.id)
                                            .projects(project.id)
                                            .tasks(task.id)
                                            .hasDependencies();

                                          setState((state) => ({
                                            ...state,
                                            disabled: hasDependencies,
                                            tooltip: hasDependencies ? 'This task is currently in use.' : null,
                                          }));
                                        }
                                      }}
                                      onClick={() => setIsOpen(false) || handleDelete(task)}>
                                      Delete
                                    </ListViewMenu.Item>

                                    <ListViewMenu.Submenu label="More Actions">
                                      {['rollup', 'milestone'].includes(task.type) ? (
                                        <ListViewMenu.Item
                                          disabled
                                          tooltip={
                                            task.type === 'milestone'
                                              ? 'Milestone tasks do not have time entries.'
                                              : 'Rollup tasks do not have time entries.'
                                          }>
                                          View Time Entries
                                        </ListViewMenu.Item>
                                      ) : (
                                        <ListViewMenu.Link
                                          to={`/app/${workspace.key}/reports/time-entries?${new QueryString({
                                            start: 'not_set',
                                            end: 'not_set',
                                            project: project.id,
                                            projectTask: task.id,
                                          })}`}>
                                          View Time Entries
                                        </ListViewMenu.Link>
                                      )}

                                      <ListViewMenu.Item
                                        disabled={
                                          !task.permissions.manage || ['rollup', 'milestone'].includes(task.type)
                                        }
                                        tooltip={(() => {
                                          if (!task.permissions.manage)
                                            return "Insufficient permissions to move this task's time entries.";
                                          if (task.type === 'rollup') return 'Rollup tasks do not have time entries.';
                                          if (task.type === 'milestone')
                                            return 'Milestone tasks do not have time entries.';
                                        })()}
                                        onClick={() => setIsOpen(false) || handleMoveTime(task)}>
                                        Move Time
                                      </ListViewMenu.Item>

                                      {features.allocations && (
                                        <ListViewMenu.Item
                                          disabled={
                                            !task.permissions.manage || ['rollup', 'milestone'].includes(task.type)
                                          }
                                          tooltip={(() => {
                                            if (!task.permissions.manage)
                                              return "Insufficient permissions to move this task's allocations.";
                                            if (task.type === 'rollup') return 'Rollup tasks do not have allocations.';
                                            if (task.type === 'milestone')
                                              return 'Milestone tasks do not have allocations.';
                                          })()}
                                          onClick={() => setIsOpen(false) || handleMoveAllocations(task)}>
                                          Move Allocations
                                        </ListViewMenu.Item>
                                      )}

                                      {['standard', 'rollup', 'milestone']
                                        .filter((targetType) => targetType !== task.type)
                                        .map((targetType) => {
                                          const blockedByHours =
                                            ['rollup', 'milestone'].includes(targetType) && task.hours > 0;
                                          const blockedBySubtasks =
                                            targetType === 'milestone' && tasks.some((t) => t.parentId === task.id);
                                          return (
                                            <ListViewMenu.Item
                                              key={targetType}
                                              disabled={!task.permissions.manage || blockedByHours || blockedBySubtasks}
                                              tooltip={(() => {
                                                if (!task.permissions.manage)
                                                  return 'Insufficient permissions to change this task category.';
                                                if (blockedByHours)
                                                  return 'Move time entries to another task before changing its category.';
                                                if (blockedBySubtasks) return 'A milestone cannot have subtasks.';
                                                return undefined;
                                              })()}
                                              onClick={() => setIsOpen(false) || handleChangeType(task, targetType)}>
                                              {`Change to ${projectTaskCategoryTypes[targetType].name} Task`}
                                            </ListViewMenu.Item>
                                          );
                                        })}

                                      {task.recordStatusId === 'active' ? (
                                        <ListViewMenu.Item
                                          disabled={!task.permissions.manage}
                                          tooltip={
                                            !task.permissions.manage
                                              ? 'Insufficient permissions to archive this project task.'
                                              : undefined
                                          }
                                          onClick={() => {
                                            setIsOpen(false);
                                            handleArchive(task);
                                          }}>
                                          Archive
                                        </ListViewMenu.Item>
                                      ) : (
                                        <ListViewMenu.Item
                                          disabled={!task.permissions.manage}
                                          tooltip={
                                            !task.permissions.manage
                                              ? 'Insufficient permissions to unarchive this project task.'
                                              : undefined
                                          }
                                          onClick={() => {
                                            setIsOpen(false);
                                            handleUnarchive(task);
                                          }}>
                                          Unarchive
                                        </ListViewMenu.Item>
                                      )}
                                    </ListViewMenu.Submenu>
                                  </>
                                )}
                              </ListViewMenu>
                            </ListViewActions>
                          </Styled.Row>
                        </>
                      );
                    }}
                    renderDragPreview={({ node }) => {
                      const task = node.task;
                      return (
                        <Styled.DragPreview>
                          <div>{task.name}</div>
                        </Styled.DragPreview>
                      );
                    }}
                  />
                );
              })}

              <ListView.Row style={{ fontWeight: weights.bold }}>
                <ListView.Cell />
                <ListView.Cell />
                <ListView.Cell />
                <ListView.Cell />
                <ListView.Cell>
                  <span style={{ paddingLeft: '1.5rem' }}>Total</span>
                </ListView.Cell>
                <ListView.Cell />
                <ListView.Cell />
                <ListView.Cell />
                <ListView.Cell />
                <ListView.Cell />
                <ListView.Cell>
                  <HoursProgress
                    worked={tasks.reduce((acc, task) => acc + (task.type === 'rollup' ? 0 : (task.hours ?? 0)), 0)}
                    estimated={tasks.reduce(
                      (acc, task) => acc + (task.type === 'rollup' ? 0 : (task.budgetHours ?? 0)),
                      0,
                    )}
                  />
                </ListView.Cell>
                <ListView.Cell>
                  <RevenueProgress
                    earned={tasks.reduce((acc, task) => acc + (task.type === 'rollup' ? 0 : (task.revenue ?? 0)), 0)}
                    budgeted={tasks.reduce(
                      (acc, task) => acc + (task.type === 'rollup' ? 0 : (task.budgetRevenue ?? 0)),
                      0,
                    )}
                    currency={currency}
                  />
                </ListView.Cell>
                <ListView.Cell />
                <ListViewActions.Cell />
              </ListView.Row>
            </ListView.Body>

            <ListView.Status total={tasks.length} label="Task" isLoading={isLoading} />
          </ListView>
        </Tree>
      </Page.ListView>

      {selection.any && project.permissions.edit && (
        <ActionsBanner>
          <BulkActionMenu count={selection.count}>
            {({ setIsOpen }) => (
              <>
                <BulkActionMenu.Item onClick={() => setIsOpen(false) || handleBulkSetStatus()}>
                  Set Status
                </BulkActionMenu.Item>
                <BulkActionMenu.Item onClick={() => setIsOpen(false) || handleBulkChangeCategory()}>
                  Change Task Category
                </BulkActionMenu.Item>
                <BulkActionMenu.Item onClick={() => setIsOpen(false) || handleBulkArchive()}>
                  Archive
                </BulkActionMenu.Item>
                <BulkActionMenu.Item onClick={() => setIsOpen(false) || handleBulkUnarchive()}>
                  Unarchive
                </BulkActionMenu.Item>
                <BulkActionMenu.Item onClick={() => setIsOpen(false) || handleBulkDelete()}>Delete</BulkActionMenu.Item>
              </>
            )}
          </BulkActionMenu>
        </ActionsBanner>
      )}

      {dialog}
    </>
  );
}

function getAllNodeIds(nodes) {
  const ids = [];
  function traverse(nodeList) {
    for (const node of nodeList) {
      ids.push(node.id);
      if (node.children?.length) traverse(node.children);
    }
  }
  traverse(nodes);
  return ids;
}
